<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const lists = computed({
  get: () => props.modelValue || [],
  set: val => emit('update:modelValue', val),
});

const isModalOpen = ref(false);
const editingIndex = ref(-1);
const listForm = ref({
  nome: '',
  items: [],
});

const openAddList = () => {
  editingIndex.value = -1;
  listForm.value = {
    nome: '',
    items: [
      { categoria: 'qualificadas', valor: '' },
      { categoria: 'nao_qualificadas', valor: '' },
    ],
  };
  isModalOpen.value = true;
};

const openEditList = (list, index) => {
  editingIndex.value = index;
  listForm.value = {
    ...list,
    items: (list.items || []).map(i => ({ ...i })),
  };
  isModalOpen.value = true;
};

const addItemCategory = () => {
  listForm.value.items.push({ categoria: '', valor: '' });
};

const removeItemCategory = idx => {
  const item = listForm.value.items[idx];
  if (item.id) {
    item._destroy = true;
  } else {
    listForm.value.items.splice(idx, 1);
  }
};

const saveList = () => {
  if (!listForm.value.nome.trim()) return;

  const res = [...lists.value];
  if (editingIndex.value >= 0) {
    res[editingIndex.value] = { ...listForm.value };
  } else {
    res.push({ ...listForm.value });
  }
  lists.value = res;
  isModalOpen.value = false;
};

const removeList = index => {
  const res = [...lists.value];
  const item = res[index];
  if (item.id) {
    item._destroy = true;
  } else {
    res.splice(index, 1);
  }
  lists.value = res;
};

const visibleLists = computed(() => {
  return lists.value.filter(l => !l._destroy);
});
</script>

<template>
  <div class="space-y-4 max-w-4xl">
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Listas de Qualificação de Leads
          </h3>
          <p class="text-xs text-n-slate-11">
            Conjuntos de valores (ex: lista de escolas, bairros, produtos)
            usados para classificar leads em quentes/frios.
          </p>
        </div>
        <Button
          sm
          icon="i-lucide-plus"
          label="Adicionar Lista"
          @click="openAddList"
        />
      </div>

      <div
        v-if="visibleLists.length === 0"
        class="text-center py-8 border border-dashed border-n-weak rounded-lg text-n-slate-11 text-sm"
      >
        Nenhuma lista de qualificação criada. Clique em "+ Adicionar Lista" para
        criar.
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(list, index) in visibleLists"
          :key="list.id || index"
          class="bg-n-surface-2 border border-n-weak rounded-xl p-4 space-y-3 hover:border-n-brand/50 transition-colors"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <Icon icon="i-lucide-list-checks" class="size-4 text-n-brand" />
              <span class="font-bold text-sm text-n-slate-12">{{
                list.nome
              }}</span>
              <span class="text-xs text-n-slate-11">({{
                  (list.items || []).filter(i => !i._destroy).length
                }}
                categorias)</span>
            </div>
            <div class="flex items-center space-x-1">
              <Button
                ghost
                slate
                sm
                icon="i-lucide-pencil"
                @click="openEditList(list, index)"
              />
              <Button
                ghost
                ruby
                sm
                icon="i-lucide-trash-2"
                @click="removeList(index)"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div
              v-for="item in (list.items || []).filter(i => !i._destroy)"
              :key="item.id || item.categoria"
              class="bg-n-surface-1 border border-n-weak rounded-lg p-2.5 space-y-1"
            >
              <span
                class="font-mono text-xs font-semibold text-n-brand uppercase"
                >{{ item.categoria }}</span>
              <p
                class="font-mono text-[11px] text-n-slate-11 whitespace-pre-line line-clamp-3"
              >
                {{ item.valor }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Adicionar / Editar Lista -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
    >
      <div
        class="bg-n-surface-1 border border-n-weak rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-surface-2"
        >
          <h3 class="font-bold text-base text-n-slate-12">
            {{
              editingIndex >= 0
                ? 'Editar Lista de Qualificação'
                : 'Nova Lista de Qualificação'
            }}
          </h3>
          <Button
            ghost
            slate
            sm
            icon="i-lucide-x"
            @click="isModalOpen = false"
          />
        </div>

        <div class="p-6 space-y-6 overflow-y-auto flex-1">
          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Nome da Lista <span class="text-red-500">*</span>
            </label>
            <input
              v-model="listForm.nome"
              type="text"
              placeholder="Ex: Escolas da Região"
              class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
              required
            />
          </div>

          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="text-xs font-bold text-n-slate-12 uppercase">
                Categorias & Itens da Lista
              </h4>
              <Button
                ghost
                sm
                icon="i-lucide-plus"
                label="Adicionar Categoria"
                @click="addItemCategory"
              />
            </div>

            <div
              v-for="(item, idx) in listForm.items.filter(i => !i._destroy)"
              :key="idx"
              class="bg-n-surface-2 border border-n-weak rounded-xl p-4 space-y-3"
            >
              <div class="flex items-center justify-between gap-3">
                <input
                  v-model="item.categoria"
                  type="text"
                  placeholder="Nome da categoria (ex: qualificadas, nao_qualificadas, infantil)"
                  class="reset-base !mb-0 w-full text-xs font-mono font-bold bg-n-surface-1 border border-n-weak rounded-lg px-3 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
                />
                <Button
                  ghost
                  ruby
                  sm
                  icon="i-lucide-trash-2"
                  @click="removeItemCategory(idx)"
                />
              </div>

              <div>
                <textarea
                  v-model="item.valor"
                  rows="4"
                  placeholder="Cole aqui os itens (um por linha)&#10;Colégio Arbos&#10;Colégio Singular&#10;Escola Villare"
                  class="reset-base !mb-0 w-full text-xs font-mono bg-n-surface-1 border border-n-weak rounded-lg p-2.5 text-n-slate-12 focus:border-n-brand outline-none resize-y leading-relaxed"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak bg-n-surface-2"
        >
          <Button ghost slate label="Cancelar" @click="isModalOpen = false" />
          <Button label="Salvar Lista" @click="saveList" />
        </div>
      </div>
    </div>
  </div>
</template>
